import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { ZoomViewer } from "./ZoomViewer";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SectionHead, EASE } from "./SectionHead";
import { asset } from "@/lib/api";
import { BlurImg } from "./BlurImg";
import { SwipeHint, useCap, ShowAll } from "./Rail";

const GRID = "grid grid-rows-[auto_auto] grid-flow-col auto-cols-[44vw] gap-x-4 gap-y-7 overflow-x-auto snap-x snap-mandatory -mx-5 px-5 pt-4 pb-5 no-scrollbar md:mx-0 md:px-2 md:overflow-visible md:snap-none md:grid-rows-none md:grid-flow-row md:auto-cols-auto md:grid-cols-3 lg:grid-cols-4 md:gap-x-8 md:gap-y-10";
const TILTS = [-2.5, 1.8, -1.2, 2.6, -1.8, 1.2, -2.2, 2];

const Stamp = ({ tag }) => (
  <span className={`shrink-0 -rotate-6 border-2 font-mono text-[10px] font-bold tracking-[0.2em] px-1.5 py-0.5 ${tag === "asset" ? "border-[#0E7490] text-[#0E7490]" : "border-[#C2410C] text-[#C2410C]"}`}>
    {tag === "asset" ? "ASSET" : "WIP"}
  </span>
);

const Polaroid = ({ w, i, onOpen, className }) => (
  <motion.div className={`snap-start ${className}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: EASE }}>
    <button type="button" data-testid={`wip-item-${w.id}`} onClick={onOpen} style={{ "--tilt": `${TILTS[i % TILTS.length]}deg` }}
      className="polaroid group relative block w-full bg-[#EDE6D3] p-2 sm:p-2.5 pb-3 text-left">
      <span aria-hidden="true" className={`tape ${w.tag === "asset" ? "tape-cyan" : ""}`} />
      <span className="block relative aspect-square overflow-hidden bg-[#12131A]">
        <BlurImg src={w.image_url} w={480} alt={w.title || "Work in progress"} imgClassName="group-hover:scale-105" />
      </span>
      <span className="mt-2.5 flex items-start justify-between gap-2">
        <span className="min-w-0 font-display font-bold uppercase text-[15px] sm:text-lg leading-tight text-[#111] truncate">{w.title || "Untitled"}</span>
        <Stamp tag={w.tag} />
      </span>
      {w.note && <span className="block mt-0.5 font-mono text-[11px] text-[#3f3f46] truncate">{w.note}</span>}
    </button>
  </motion.div>
);

const FILTERS = [["all", "All"], ["wip", "WIP"], ["asset", "Assets"]];
const TagChip = ({ tag }) => (
  <span className={`font-mono text-[10px] font-bold tracking-[0.25em] px-2 py-1 ${tag === "asset" ? "bg-[#00F0FF] text-black" : "bg-[#F5A524] text-black"}`}>
    {tag === "asset" ? "ASSET" : "WIP"}
  </span>
);

export const Wip = ({ index, items }) => {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [zoomAt, setZoomAt] = useState(null);
  const list = filter === "all" ? items : items.filter((w) => w.tag === filter);
  const cur = open != null ? list[open] : null;
  const step = (d) => setOpen((o) => (o + d + list.length) % list.length);
  const cap = useCap(list.length, 8);

  return (
    <section id="wip" data-testid="wip-section" className="pinboard relative px-5 sm:px-10 lg:px-16 py-16 md:py-24 border-t border-[#F5A524]/15 bg-[#0E0F14]">
      <SectionHead index={index} kicker="WORK IN PROGRESS / ASSETS" title={<>On the<br /><span className="text-[#F5A524]">Desk</span></>}>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`wip-filter-${k}`} onClick={() => setFilter(k)}
              className={`clip-tab font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors ${filter === k ? "bg-[#F5A524] text-black" : "bg-[#12131A] text-zinc-300 hover:text-[#F5A524] border border-[#F5A524]/20"}`}>
              {l}<span className="ml-2 opacity-60">{k === "all" ? items.length : items.filter((w) => w.tag === k).length}</span>
            </button>
          ))}
        </div>
      </SectionHead>
      <SwipeHint count={list.length} testid="wip-swipe-hint" />
      <div data-testid="wip-grid" className={GRID}>
        {list.map((w, i) => <Polaroid key={w.id} w={w} i={i} onOpen={() => setOpen(i)} className={cap.hidden(i)} />)}
      </div>
      {list.length === 0 && <p className="font-mono text-sm text-zinc-400">NOTHING TAGGED HERE YET.</p>}
      <ShowAll cap={cap} total={list.length} testid="wip-show-all" anchor="wip" />

      <Dialog open={!!cur} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="wip-lightbox" className="bg-[#0B0C10] border-[#F5A524]/40 rounded-none max-w-5xl p-0 gap-0">
          {cur && (
            <>
              <DialogTitle className="flex items-center gap-3 px-5 py-3 border-b border-[#F5A524]/20 font-display font-black uppercase text-2xl">
                <TagChip tag={cur.tag} /> {cur.title || "Untitled"}
              </DialogTitle>
              <div className="relative bg-black">
                <img src={asset(cur.image_url)} alt={cur.title} onClick={() => { setZoomAt(open); setOpen(null); }} data-testid="wip-lightbox-image"
                  className="w-full max-h-[70vh] object-contain cursor-zoom-in" />
                <button data-testid="wip-fullscreen-btn" onClick={() => { setZoomAt(open); setOpen(null); }} aria-label="View full screen"
                  className="absolute bottom-3 right-3 flex items-center gap-2 bg-black/75 border border-[#F5A524]/50 px-3 py-2 font-mono text-[11px] tracking-[0.2em] text-[#F5A524] hover:bg-[#F5A524] hover:text-black transition-colors">
                  <Maximize2 size={14} /> FULL SCREEN
                </button>
                {list.length > 1 && (
                  <>
                    <button data-testid="wip-lightbox-prev" onClick={() => step(-1)} aria-label="Previous" className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-black/70 text-[#F5A524] hover:bg-[#F5A524] hover:text-black"><ChevronLeft /></button>
                    <button data-testid="wip-lightbox-next" onClick={() => step(1)} aria-label="Next" className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-black/70 text-[#F5A524] hover:bg-[#F5A524] hover:text-black"><ChevronRight /></button>
                  </>
                )}
              </div>
              {cur.note && <p data-testid="wip-lightbox-note" className="px-5 py-4 text-sm text-zinc-300 whitespace-pre-line">{cur.note}</p>}
            </>
          )}
        </DialogContent>
      </Dialog>
      {zoomAt != null && <ZoomViewer images={list.map((w) => w.image_url)} titles={list.map((w) => w.title || (w.tag === "asset" ? "Asset" : "Work in progress"))} start={zoomAt} title="Work in progress" onClose={() => setZoomAt(null)} />}
    </section>
  );
};
